import { useMemo, useState } from "react";
import { getBusinessCategoryLabel, getBusinessCategoryShortLabel } from "../constants/businessCategories";
import { useTranslation } from "../context/TranslationContext";
import { SpaceTile } from "./discover/DiscoverAll";
import Avatar from "./ds/Avatar";
import Badge from "./ds/Badge";
import Button from "./ds/Button";
import EmptyState from "./ds/EmptyState";
import Icon from "./ds/Icon";
import Page from "./ds/Page";
import PageHeader from "./ds/PageHeader";
import SearchField from "./ds/SearchField";
import { Stagger, StaggerItem } from "./motion/Reveal";

/** Figma B4 "Business": logo, name and category, members, and the way in. */
function BusinessPickCard({ space, onSelect, t, actionLabel }) {
  const memberCount = space.members?.length ?? 0;

  return (
    <StaggerItem>
      <button
        type="button"
        onClick={() => onSelect(space._id)}
        className="flex h-full w-full flex-col gap-space-16 rounded-radius-lg bg-bg-raised p-space-16 text-left outline-none focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
      >
        <span className="flex min-w-0 items-center gap-space-12">
          <Avatar size="l" src={space.logo} name={space.name} />
          <span className="flex min-w-0 flex-1 flex-col gap-space-4">
            <span className="text-caps-label uppercase text-accent-text">{t("windows.yourBusinessBadge")}</span>
            <span className="truncate text-headline text-label-primary">{space.name}</span>
            {space.category && (
              <span className="line-clamp-2 text-footnote text-label-secondary">{getBusinessCategoryLabel(t, space.category)}</span>
            )}
          </span>
        </span>
        <span className="mt-auto flex items-center justify-between gap-space-8">
          <span className="inline-flex items-center gap-space-4 text-footnote text-label-secondary">
            <Icon name="users" size={16} />
            {memberCount}
          </span>
          <span className="inline-flex h-button-s items-center gap-space-4 rounded-radius-md bg-accent-tinted px-space-12 text-caps-button-s uppercase text-accent-tinted-label">
            {actionLabel}
            <Icon name="chevron-right" size={16} />
          </span>
        </span>
      </button>
    </StaggerItem>
  );
}

function CreateNewBusinessCard({ onCreate, t }) {
  return (
    <StaggerItem>
      <button
        type="button"
        onClick={onCreate}
        className="flex h-full min-h-[150px] w-full flex-col items-center justify-center gap-space-8 rounded-radius-lg bg-bg-raised p-space-16 text-center outline-none hover:bg-fill-quaternary focus-visible:shadow-[0_0_0_2px_var(--color-focus-ring)]"
      >
        <span className="flex size-12 items-center justify-center rounded-14 bg-accent-tinted text-accent-tinted-label">
          <Icon name="plus" size={24} />
        </span>
        <span className="text-headline text-label-primary">{t("windows.createNewBusiness")}</span>
        <span className="text-footnote text-label-secondary">{t("windows.createNewBusinessHint")}</span>
      </button>
    </StaggerItem>
  );
}

/** Figma B4: pick one of your businesses, to open it or to run a campaign under it. */
export default function SelectCampaignMintSpace({ mintSpaces, loading, onSelectSpace, onCreateBusiness, t, variant = "campaign" }) {
  const { language } = useTranslation();
  const isBusinessFlow = variant === "business";
  const [query, setQuery] = useState("");

  const owned = useMemo(() => mintSpaces.filter((space) => space.isOwner), [mintSpaces]);

  const q = query.trim().toLowerCase();
  const shown = q
    ? owned.filter(
        (space) =>
          space.name?.toLowerCase().includes(q) || getBusinessCategoryLabel(t, space.category).toLowerCase().includes(q)
      )
    : owned;

  if (loading) {
    return <p className="py-space-40 text-center text-subheadline text-label-secondary">{t("windows.loadingMintSpaces")}</p>;
  }

  if (owned.length === 0) {
    return (
      <Page>
        <EmptyState
          icon="plus"
          title={t("windows.noMintSpacesYet")}
          message={t("windows.mintSpaceDescription")}
          actionLabel={t("navigation.createBusiness")}
          onAction={onCreateBusiness}
        />
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        eyebrow={isBusinessFlow ? t("sidebar.yourBusinesses") : t("windows.creatorRewards")}
        title={
          isBusinessFlow
            ? `${t("windows.selectBusinessHero")} ${t("windows.selectBusinessHeroAccent")}`
            : `${t("windows.selectCampaignHero")} ${t("windows.selectCampaignHeroAccent")}`
        }
        subtitle={isBusinessFlow ? t("windows.selectBusinessSubtitle") : t("windows.selectCampaignSubtitle")}
      />

      {!isBusinessFlow && (
        <div className="flex flex-wrap items-center gap-space-8">
          {[t("windows.stepPick"), t("windows.stepBuild"), t("windows.stepLaunch")].map((label, index) => (
            <Badge key={label} tone={index === 0 ? "success" : "neutral"}>
              {index + 1} · {label}
            </Badge>
          ))}
        </div>
      )}

      {owned.length > 4 && (
        <SearchField
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("windows.searchBusinesses")}
          className="max-w-[320px]"
        />
      )}

      {shown.length > 0 ? (
        <section className="flex flex-col gap-space-24">
          <div className="flex flex-col gap-space-4">
            <h2 className="text-title-3 text-label-primary">{t("sidebar.yourBusinesses")}</h2>
            <p className="text-footnote text-label-secondary">
              {isBusinessFlow ? t("windows.manageBusinessesHint") : t("windows.ownedSpacesHint")}
            </p>
          </div>
          {isBusinessFlow ? (
            <Stagger className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-space-16 lg:gap-space-24" stagger={0.07}>
              <CreateNewBusinessCard onCreate={onCreateBusiness} t={t} />
              {shown.map((space) => (
                <StaggerItem key={space._id} className="flex h-full min-h-0">
                  <SpaceTile
                    space={space}
                    categoryLabel={space.category ? getBusinessCategoryShortLabel(t, space.category) : ""}
                    isMine
                    onOpen={(picked) => onSelectSpace(picked._id)}
                    language={language}
                    t={t}
                  />
                </StaggerItem>
              ))}
            </Stagger>
          ) : (
            <Stagger className="grid grid-cols-1 gap-space-24 min-[800px]:grid-cols-2" stagger={0.07}>
              {shown.map((space) => (
                <BusinessPickCard
                  key={space._id}
                  space={space}
                  onSelect={onSelectSpace}
                  t={t}
                  actionLabel={t("windows.launchHere")}
                />
              ))}
            </Stagger>
          )}
        </section>
      ) : (
        <div className="flex flex-col items-center gap-space-16 py-space-24">
          <p className="text-subheadline text-label-secondary">{t("windows.noSearchResults")}</p>
          <Button size="m" icon={<Icon name="plus" size={20} />} onClick={onCreateBusiness}>
            {t("navigation.createBusiness")}
          </Button>
        </div>
      )}
    </Page>
  );
}
